import React, { useEffect, useState } from 'react'
import { supabase } from '../supabaseClient'

// Private rivalry leagues over the ONE global LMS pool -- per
// MINI_LEAGUES_SPEC.md's hybrid model. No pot/fee/money at this level,
// ever; this panel only ever creates/joins/lists lms_leagues rows and never
// touches lms_entries, lms_rounds, or anything money-related.
export default function LeaguesPanel({ eventId }) {
  const [leagues, setLeagues] = useState([])
  const [loading, setLoading] = useState(true)
  const [mode, setMode] = useState(null) // null | 'create' | 'join'
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [copiedId, setCopiedId] = useState(null)

  async function loadLeagues() {
    setLoading(true)
    // RLS (leagues_read_member) already scopes this to leagues I'm in, so no
    // RPC needed for a plain list.
    const { data } = await supabase
      .from('lms_leagues')
      .select('id, name, invite_code')
      .eq('event_id', eventId)
      .order('created_at', { ascending: false })
    setLeagues(data || [])
    setLoading(false)
  }

  useEffect(() => {
    loadLeagues()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId])

  async function handleCreate(e) {
    e.preventDefault()
    if (!name.trim()) return
    setBusy(true)
    setError('')
    const { data, error: createError } = await supabase.rpc('create_league', {
      p_event_id: eventId,
      p_name: name.trim()
    })
    setBusy(false)
    if (createError) { setError(createError.message); return }
    setName('')
    setMode(null)
    setLeagues(prev => [data, ...prev])
  }

  async function handleJoin(e) {
    e.preventDefault()
    if (!code.trim()) return
    setBusy(true)
    setError('')
    const { data, error: joinError } = await supabase.rpc('join_league_by_code', {
      p_code: code.trim()
    })
    setBusy(false)
    if (joinError) { setError('That invite code is not valid. Double-check it and try again.'); return }
    setCode('')
    setMode(null)
    setLeagues(prev => prev.some(l => l.id === data.id) ? prev : [data, ...prev])
  }

  async function handleCopyCode(e, league) {
    e.preventDefault()
    e.stopPropagation()
    try {
      await navigator.clipboard.writeText(league.invite_code)
      setCopiedId(league.id)
      setTimeout(() => setCopiedId(prev => (prev === league.id ? null : prev)), 2000)
    } catch {
      // Clipboard API unavailable (e.g. non-HTTPS or older mobile browser) --
      // the code is already visible on screen, so this is a soft failure.
    }
  }

  return (
    <div className="card" style={{ marginBottom: '20px' }}>
      <div className="flex-between" style={{ marginBottom: '14px' }}>
        <span style={{ fontFamily: 'var(--ff-d)', fontSize: '16px', fontWeight: '700' }}>My Leagues</span>
        {!mode && (
          <div className="flex gap-8">
            <button className="btn btn-secondary" style={{ padding: '6px 14px', fontSize: '12px' }} onClick={() => { setMode('join'); setError('') }}>
              Join
            </button>
            <button className="btn btn-primary" style={{ padding: '6px 14px', fontSize: '12px' }} onClick={() => { setMode('create'); setError('') }}>
              Create
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <p className="text-muted" style={{ fontSize: '13px' }}>Loading leagues...</p>
      ) : (
        <>
          {leagues.length === 0 && !mode && (
            <p className="text-muted" style={{ fontSize: '13px' }}>
              No leagues yet. Create one to start a private board with friends, or join with an invite code.
            </p>
          )}

          {leagues.map(l => (
            <a
              key={l.id}
              href={`/lms/${eventId}/leagues/${l.id}`}
              className="card-flat"
              style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '12px 16px', marginBottom: '8px', textDecoration: 'none', color: 'inherit'
              }}
            >
              <span style={{ fontWeight: '600', fontSize: '14px' }}>{l.name}</span>
              <span
                className="badge-gray"
                onClick={e => handleCopyCode(e, l)}
                title="Copy invite code"
                style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '5px' }}
              >
                {copiedId === l.id ? 'Copied!' : <>{l.invite_code}<span aria-hidden="true" style={{ opacity: 0.7 }}>⧉</span></>}
              </span>
            </a>
          ))}
        </>
      )}

      {mode === 'create' && (
        <form onSubmit={handleCreate} style={{ marginTop: '12px' }}>
          <label className="label" style={{ display: 'block', marginBottom: '6px' }}>League name</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. The Office League"
            maxLength={60}
            autoFocus
            style={{ marginBottom: '10px' }}
          />
          <div className="flex gap-8">
            <button type="submit" className="btn btn-primary" disabled={busy || !name.trim()}>
              {busy ? 'Creating...' : 'Create league'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => { setMode(null); setError('') }}>Cancel</button>
          </div>
        </form>
      )}

      {mode === 'join' && (
        <form onSubmit={handleJoin} style={{ marginTop: '12px' }}>
          <label className="label" style={{ display: 'block', marginBottom: '6px' }}>Invite code</label>
          <input
            type="text"
            value={code}
            onChange={e => setCode(e.target.value.toUpperCase())}
            placeholder="e.g. 7K9PXM"
            maxLength={6}
            autoFocus
            style={{ marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: 'var(--ff-d)' }}
          />
          <div className="flex gap-8">
            <button type="submit" className="btn btn-primary" disabled={busy || !code.trim()}>
              {busy ? 'Joining...' : 'Join league'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => { setMode(null); setError('') }}>Cancel</button>
          </div>
        </form>
      )}

      {error && <p style={{ color: 'var(--red)', fontSize: '13px', marginTop: '10px' }}>{error}</p>}
    </div>
  )
}
